import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  Fragment,
} from "react";
import PropTypes from "prop-types";
import { navigate } from "gatsby";
// import Breadcrumb from "src/atoms/Breadcrumb";
import styled, { css, keyframes } from "styled-components";
import { colors, mediaquery } from "src/styles/variables";
import Button from "src/atoms/Button";
import { HeaderL, Body } from "src/atoms/Typography";
import { InView } from "react-intersection-observer";
import PageContext from "src/stores/Page";
import ProgressBarTracker from "src/atoms/ProgressBarTracker";
import { Container, Col, Row } from "react-awesome-styled-grid";
import HeaderCurved from "src/atoms/HeaderCurved/HeaderCurved";
import RichText from "src/atoms/RichText";

import TrackerQuestions from "./TrackerQuestions";
import TrackerCategories from "./TrackerCategories";

import getImage from "src/utils/getImage";
import Picture, { FITS, TYPES as PICTURE_TYPES } from "src/atoms/Picture";
import Settings from "src/stores/Settings";
import TrackerForm from "../TrackerForm";
import { generateUUID } from "src/utils/generateUUID";

import trackEvent from "./trackEvent";

const Tracker = ({
  id,
  title,
  trackerDescription,
  imageDesktop,
  imageMobile,
  trackingStartCode,
  generalTrackingAreaCode,
  categoryTrackingEndValue,
  introductionSlideText,
  introductionSlideImageDesktop,
  introductionSlideImageMobile,
  trackerForm,
  categories,
  thanksSlideText,
  thanksSlideImageDesktop,
  thanksSlideImageMobile,
  resultPageSlug,
  showResultsTrackingCode,
}) => {
  const TrackerListRef = useRef(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [LatestItemIndex, setLatestItemIndex] = useState(0);
  const [validFormData, setValidFormData] = useState(false);
  const [formIsDirty, setFormIsDirty] = useState(false);
  const [ioMargin, setIoMargin] = useState(0);
  const [showApiError, setShowApiError] = useState(false);
  const {
    //transparentHeader: [, setTransparentHeader],
    forceVisibleHeader: [, setForceVisibleHeader],
    headerHeight: [headerHeight],
  } = useContext(PageContext);

  const isFormInStart = trackerForm?.formPosition === "Start";
  const isFormInEnd = trackerForm?.formPosition === "End";

  const { locale } = useContext(Settings);
  const { nextTitle, previousTitle, showTrackerResultsTitle } = useContext(
    Settings
  ).translations;

  const trackerListItemsArr = [].concat(
    ...categories.map((item) => {
      return [
        item.id,
        ...item.listOfQuestions.map((subItem) => {
          return subItem.id;
        }),
      ];
    })
  );

  const trackerProgressSegmentsCount =
    categories.length + (isFormInEnd ? 2 : 1);
  let accTrackerProgress =
    100 / trackerProgressSegmentsCount / (isFormInStart ? 3 : 2);
  let trackerProgressArray = [
    { id: "tracker-start", type: "category", progress: 0 },
    {
      id: "tracker-intro",
      type: "question",
      progress: accTrackerProgress,
    },
  ];

  if (isFormInStart) {
    const progress = 100 / trackerProgressSegmentsCount / 3;
    accTrackerProgress += progress;
    trackerProgressArray.push({
      id: "tracker-form",
      type: "form",
      progress: accTrackerProgress,
    });
  }

  categories.map((item, index) => {
    const progress =
      index === 0
        ? 100 / trackerProgressSegmentsCount / (isFormInStart ? 3 : 2)
        : 100 /
          trackerProgressSegmentsCount /
          (categories[index - 1].listOfQuestions.length + 1);
    accTrackerProgress += progress;
    trackerProgressArray.push({
      id: item.id,
      type: "category",
      progress: accTrackerProgress,
    });
    item.listOfQuestions.map((subItem) => {
      const progress =
        100 / trackerProgressSegmentsCount / (item.listOfQuestions.length + 1);
      accTrackerProgress += progress;
      trackerProgressArray.push({
        id: subItem.id,
        type: "answer",
        progress: accTrackerProgress,
      });
    });
  });

  if (isFormInEnd) {
    const progress =
      100 /
      trackerProgressSegmentsCount /
      (categories[categories.length - 1].listOfQuestions.length + 1);
    accTrackerProgress += progress;
    trackerProgressArray.push({
      id: "tracker-form",
      type: "form",
      progress: accTrackerProgress,
    });
  }

  trackerProgressArray.push({
    id: "tracker-thanks",
    type: "category",
    progress: 100,
  });

  const disableNextBtn = (activeItemIndex) => {
    const isNotFirstOrSecondItem =
      activeItemIndex !== 0 && activeItemIndex !== 1;

    const isNotCategory =
      trackerProgressArray[activeItemIndex].type !== "category";
    const isPastLatestItem = activeItemIndex > LatestItemIndex;

    let isDisabled =
      isNotFirstOrSecondItem && isNotCategory && isPastLatestItem;

    if (trackerProgressArray[activeItemIndex].type === "form") {
      const isFormInvalid = trackerForm.optionalForm
        ? formIsDirty && !validFormData
        : !validFormData;
      isDisabled = isFormInvalid;
    }

    return isDisabled;
  };

  const ScrollToElement = (elemIndex) => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    TrackerListRef.current?.scrollTo({
      //top: document.getElementById(`tracker_item_${elemIndex}`)?.offsetTop,
      left: document.getElementById(`tracker_item_${elemIndex}`)?.offsetLeft,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    ScrollToElement(activeItemIndex);
  }, [activeItemIndex]);

  const findElementIndex = (elemId) => {
    if (!TrackerListRef.current) return;
    return trackerListItemsArr.indexOf(elemId) + 1;
  };

  const handleSubmitUserData = async () => {
    const finalJson = {
      formData: {
        user: {
          firstName: validFormData["firstName"],
          lastName: validFormData["lastName"],
          phoneNumber: validFormData["phoneNumber"],
          email: validFormData["email"],
          emailVerified: false,
          emailSubscriptionConsent:
            validFormData["emailSubscriptionConsent"] === true,
        },
      },
      uniqueFormInstanceId: generateUUID(),
      formId: id,
      formVersion: "V1.0.0",
      formSubmitTimeStamp: new Date().toISOString(),
      regionCode: process.env.GATSBY_REGION,
    };

    try {
      const response = await fetch("/.netlify/functions/holograph", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalJson),
      });

      if (response.status === 200 || response.status === 204) {
        console.log("Form successfully submitted");
        return true;
      } else {
        console.error("Failed to submit form", response.statusText);
        setShowApiError(true);
        return false;
      }
    } catch (error) {
      console.error("Error submitting form", error);
      setShowApiError(true);
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const scores = calculatePrettyScores();
    const myForm = event.target;

    trackEvent(showResultsTrackingCode, generalTrackingAreaCode);

    const formData = new FormData(myForm);

    const formDataJson = {};
    formData.forEach((value, key) => {
      if (key !== "User" && key !== "bot-field" && key !== "form-name") {
        formDataJson[key] = value.trim();
      }
    });

    const finalJson = {
      formData: {
        user: {
          firstName: validFormData["firstName"],
          lastName: validFormData["lastName"],
          phoneNumber: validFormData["phoneNumber"],
          email: validFormData["email"],
          emailVerified: false,
          emailSubscriptionConsent:
            validFormData["emailSubscriptionConsent"] === true,
        },
        totalScore: Math.round(scores.total),
        categories: [],
      },
      uniqueFormInstanceId: generateUUID(),
      formId: id,
      formVersion: "V1.0.0",
      formSubmitTimeStamp: new Date().toISOString(),
      regionCode: process.env.GATSBY_REGION,
    };

    // Group questions by category
    const categories = {};
    Object.keys(formDataJson).forEach((key) => {
      const [category, question] = key.split(" | ");
      // Initialize category in object
      if (!categories[category]) {
        categories[category] = {
          name: category,
          questions: [],
          score: 0,
        };
      }

      const categoryIndex = Object.keys(categories).indexOf(category) + 1;
      if (question && question.includes("answer weight")) {
        categories[category].score = scores[category];
        categories[category].id = categoryIndex;
      } else {
        const questionIndex = categories[category].questions.length + 1;
        const uniqueId = categoryIndex * 100 + questionIndex; // Generate a unique ID
        categories[category].questions.push({
          id: uniqueId,
          question: question,
          answer: formDataJson[key],
        });
      }
    });

    finalJson.formData.categories = Object.values(categories);

    if (trackerForm) {
      try {
        const response = await fetch("/.netlify/functions/holograph", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(finalJson),
        });

        if (response.status === 200 || response.status === 204) {
          console.log("Form successfully submitted");
          goToResults();
        } else {
          console.error("Failed to submit form", response.statusText);
          setShowApiError(true);
        }
      } catch (error) {
        console.error("Error submitting form", error);
        setShowApiError(true);
      }
    } else {
      goToResults();
    }
  };

  useEffect(() => {
    setIoMargin(window.innerHeight - headerHeight);
  }, [headerHeight]);

  const answersCategories = {};
  categories.map((item) => {
    answersCategories[item.id] = [];
  });
  const [answers, setAnswers] = useState(answersCategories);
  let newAnswersArr = answersCategories;

  const setSelectedAnswer = (id, questionId, answerIndex, score) => {
    newAnswersArr = answers;
    const newsAnswersObj = { questionId, answerIndex, score };
    const arrayIndex = newAnswersArr[id].findIndex(
      (subItem) => subItem.questionId === questionId
    );

    if (arrayIndex !== -1) {
      newAnswersArr[id].splice(arrayIndex, 1);
    }
    newAnswersArr[id].push(newsAnswersObj);
    setAnswers(newAnswersArr);
    if (activeItemIndex > LatestItemIndex) {
      setLatestItemIndex(activeItemIndex);
    }
    /*
    @TODO we need to read this in and set it as answers if it exists
    if client refreshes page closes browser etc.
    */
    localStorage.setItem("answers", JSON.stringify(answers));
  };

  const calculatePrettyScores = () => {
    let result = {};
    let total = 0;
    categories.map((category) => {
      const categoryScore = answers[category.id].reduce((acc, curr) => {
        return acc + parseInt(curr.score, 10);
      }, 0);
      const weightedCategoryScore = Math.round(
        categoryScore * category.categoryScoreFactor
      );

      result[category.title] = weightedCategoryScore;
      total = total + weightedCategoryScore;
    });
    result["total"] = total;
    return result;
  };

  const goToResults = () => {
    let queryStr = "?";

    categories.map((category, index) => {
      const categoryScore = answers[category.id].reduce((acc, curr) => {
        return acc + parseInt(curr.score, 10);
      }, 0);
      queryStr += `${category.id}=${Math.round(
        categoryScore * category.categoryScoreFactor
      )}${index !== categories.length - 1 ? "&" : ""}`;
    });

    navigate(
      `${locale.currentLanguage.pathPrefix ?? ""}${resultPageSlug}${queryStr}`,
      {
        state: {
          trackerUrl: window.location.href,
        },
      }
    );
  };

  return (
    <InView
      rootMargin={`0px 0px -${ioMargin}px 0px`}
      onChange={(inView) => {
        //setTransparentHeader(inView);
        setForceVisibleHeader(inView);
      }}
    >
      <ProgressBarWrapper activeItemIndex={activeItemIndex}>
        <Container>
          <NoMarginRowStyled>
            <Col md={10} offset={{ md: 3 }}>
              <ProgressBarTracker
                activeItemIndex={activeItemIndex}
                categories={categories}
                trackerProgressItem={trackerProgressArray[activeItemIndex]}
                isFormInEnd={isFormInEnd}
              />
            </Col>
          </NoMarginRowStyled>
        </Container>
      </ProgressBarWrapper>
      <TrackerWrapper
        name="Tracker"
        method="POST"
        onSubmit={handleSubmit}
        data-netlify="true"
        data-netlify-honeypot="bot-field"
      >
        <p className="screen-reader-text">
          <label>
            Don&apos;t fill this out if you&apos;re human:{" "}
            <input name="bot-field" />
          </label>
        </p>
        <input type="hidden" name="User" value="Tracker" />
        <TrackerQuestionsWrapper>
          <TrackerList ref={TrackerListRef}>
            <TrackerListItem id="tracker_item_0">
              <PictureWrapper
                fit={FITS.cover}
                small={imageMobile}
                large={imageDesktop}
              />
              <HeaderCurvedStyled
                fixedHeight={false}
                backgroundColor={colors.activiaGreen}
              >
                <Container>
                  <NoMarginRowStyled>
                    <Col md={10} offset={{ md: 3 }}>
                      <ContainerAnimation>
                        {/* <Breadcrumb /> */}
                        {title && <SlideHeader as="h1">{title}</SlideHeader>}
                        {trackerDescription && (
                          <SlideDescription>
                            {trackerDescription}
                          </SlideDescription>
                        )}
                      </ContainerAnimation>
                    </Col>
                  </NoMarginRowStyled>
                </Container>
              </HeaderCurvedStyled>
            </TrackerListItem>
            <TrackerListItem id="tracker_item_1">
              {introductionSlideImageMobile &&
                introductionSlideImageDesktop && (
                  <PictureWrapper
                    fit={FITS.cover}
                    small={introductionSlideImageMobile}
                    large={introductionSlideImageDesktop}
                  />
                )}
              <HeaderCurvedStyled
                fixedHeight={false}
                backgroundColor={colors.activiaGreen}
              >
                <Container>
                  <NoMarginRowStyled>
                    <Col md={10} offset={{ md: 3 }}>
                      {introductionSlideText && (
                        <RichTextWrapper>
                          <RichText
                            as="div"
                            enableRenderer
                            headColor={colors.white}
                            bodyColor={colors.white}
                            doc={introductionSlideText.value}
                          />
                        </RichTextWrapper>
                      )}
                    </Col>
                  </NoMarginRowStyled>
                </Container>
              </HeaderCurvedStyled>
            </TrackerListItem>
            {isFormInStart && (
              <TrackerListItem id="tracker_item_2">
                <TrackerForm
                  {...trackerForm}
                  onFormValidityChange={(formData) =>
                    setValidFormData(formData)
                  }
                  formIsDirty={(dirty) => setFormIsDirty(dirty)}
                  currentlyShown={activeItemIndex === 2}
                  apiError={showApiError}
                />
              </TrackerListItem>
            )}
            {categories?.map((item, index) => {
              return (
                <Fragment key={`tracker_item_${item.id}`}>
                  <TrackerListItem
                    id={`tracker_item_${
                      findElementIndex(item.id) + (isFormInStart ? 2 : 1)
                    }`}
                  >
                    <TrackerCategories
                      backgroundColor={item.colour}
                      title={`${index + 1}. ${item.title}`}
                      subtitle={item.subtitle}
                      description={item.description}
                      imageMobile={getImage(
                        item.image.small,
                        PICTURE_TYPES.fluid
                      )}
                      imageDesktop={getImage(
                        item.image.large,
                        PICTURE_TYPES.fluid
                      )}
                    />
                  </TrackerListItem>
                  {item?.listOfQuestions?.map((subItem, index) => {
                    const itemID =
                      findElementIndex(subItem.id) + (isFormInStart ? 2 : 1);
                    return (
                      <TrackerListItem
                        key={`tracker_item_${item.id}_question_${subItem.id}`}
                        id={`tracker_item_${itemID}`}
                      >
                        <TrackerQuestions
                          category={item.title}
                          backgroundColor={item.colour}
                          title={subItem.title}
                          questionDescription={subItem.questionDescription}
                          questionAnswers={subItem.questionAnswers}
                          setSelectedAnswer={setSelectedAnswer}
                          categoryId={item.id}
                          questionId={subItem.id}
                          questionIndex={index + 1}
                          questionsCount={item.listOfQuestions.length}
                          currentlyShown={activeItemIndex === itemID}
                        />
                      </TrackerListItem>
                    );
                  })}
                </Fragment>
              );
            })}
            {isFormInEnd && (
              <TrackerListItem
                id={`tracker_item_${trackerListItemsArr.length + 2}`}
              >
                <TrackerForm
                  {...trackerForm}
                  onFormValidityChange={(formData) =>
                    setValidFormData(formData)
                  }
                  formIsDirty={(dirty) => setFormIsDirty(dirty)}
                  currentlyShown={
                    activeItemIndex === trackerListItemsArr.length + 2
                  }
                />
              </TrackerListItem>
            )}
            <TrackerListItem
              id={`tracker_item_${
                trackerListItemsArr.length + (trackerForm ? 3 : 2)
              }`}
            >
              {thanksSlideImageMobile && thanksSlideImageDesktop && (
                <PictureWrapper
                  fit={FITS.cover}
                  small={thanksSlideImageMobile}
                  large={thanksSlideImageDesktop}
                />
              )}
              <HeaderCurvedStyled fixedHeight={false}>
                <Container>
                  <NoMarginRowStyled>
                    <Col md={10} offset={{ md: 3 }}>
                      {thanksSlideText && (
                        <RichTextWrapper>
                          <RichText
                            as="div"
                            enableRenderer
                            headColor={colors.white}
                            bodyColor={colors.white}
                            doc={thanksSlideText.value}
                          />
                        </RichTextWrapper>
                      )}
                    </Col>
                  </NoMarginRowStyled>
                </Container>
              </HeaderCurvedStyled>
            </TrackerListItem>
          </TrackerList>
        </TrackerQuestionsWrapper>
        <BtnsContainer>
          <NoMarginRowStyled>
            <Col md={10} offset={{ md: 3 }}>
              <CTAButtonWrapper>
                {activeItemIndex !== 0 && activeItemIndex !== -1 && (
                  <ButtonStyled
                    type="button"
                    onClick={() => {
                      setActiveItemIndex(activeItemIndex - 1);
                    }}
                  >
                    {previousTitle}
                  </ButtonStyled>
                )}
                {activeItemIndex <
                  trackerListItemsArr.length + (trackerForm ? 3 : 2) && (
                  <ButtonStyled
                    type="button"
                    disabled={disableNextBtn(activeItemIndex)}
                    onClick={async () => {
                      const currentProgressItem =
                        trackerProgressArray[activeItemIndex];

                      // Only track the very first click to start the tracker
                      if (activeItemIndex === 0) {
                        trackEvent(trackingStartCode, generalTrackingAreaCode);
                      }

                      // Track category completions
                      if (currentProgressItem.type === "answer") {
                        const currentCategory = categories.find((cat) =>
                          cat.listOfQuestions.some(
                            (q) => q.id === currentProgressItem.id
                          )
                        );

                        if (currentCategory) {
                          const currentQuestionIndex = currentCategory.listOfQuestions.findIndex(
                            (q) => q.id === currentProgressItem.id
                          );

                          // If this is the last question in the category
                          if (
                            currentQuestionIndex ===
                            currentCategory.listOfQuestions.length - 1
                          ) {
                            const categoryIndex =
                              categories.findIndex(
                                (cat) => cat.id === currentCategory.id
                              ) + 1;
                            const trackerArea = `${String(categoryIndex)}_${
                              currentCategory.categoryTrackingValue
                            }`;
                            trackEvent(categoryTrackingEndValue, trackerArea);
                          }
                        }
                      }

                      if (
                        trackerProgressArray[activeItemIndex].type ===
                        "category"
                      ) {
                        setActiveItemIndex(activeItemIndex + 1);
                      } else if (
                        trackerProgressArray[activeItemIndex].type === "form"
                      ) {
                        formIsDirty
                          ? (await handleSubmitUserData()) &&
                            setActiveItemIndex(activeItemIndex + 1)
                          : setActiveItemIndex(activeItemIndex + 1);
                      } else {
                        setActiveItemIndex(activeItemIndex + 1);
                      }
                    }}
                  >
                    {nextTitle}
                  </ButtonStyled>
                )}
                {activeItemIndex ===
                  trackerListItemsArr.length + (trackerForm ? 3 : 2) && (
                  <ButtonStyled type="submit">
                    {showTrackerResultsTitle}
                  </ButtonStyled>
                )}
              </CTAButtonWrapper>
            </Col>
          </NoMarginRowStyled>
        </BtnsContainer>
        <input type="hidden" name="form-name" value="Tracker" />
      </TrackerWrapper>
    </InView>
  );
};

Tracker.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  trackerDescription: PropTypes.string.isRequired,
  introductionSlideText: PropTypes.object,
  introductionSlideImageMobile: PropTypes.object,
  introductionSlideImageDesktop: PropTypes.object,
  trackerForm: PropTypes.object,
  imageDesktop: PropTypes.object,
  imageMobile: PropTypes.object,
  categories: PropTypes.arrayOf(TrackerCategories).isRequired,
  thanksSlideText: PropTypes.object,
  thanksSlideImageDesktop: PropTypes.object,
  thanksSlideImageMobile: PropTypes.object,
  resultPageSlug: PropTypes.string,
  trackingStartCode: PropTypes.string,
  generalTrackingAreaCode: PropTypes.string,
  categoryTrackingEndValue: PropTypes.string,
  showResultsTrackingCode: PropTypes.string,
};

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const fadeInAnimation = css`
  animation: 1s ${fadeIn} cubic-bezier(0.47, 0, 0.37, 1) forwards;
`;

const ContainerAnimation = styled.div`
  ${fadeInAnimation};
`;

const NoMarginRowStyled = styled(Row)``;

const ProgressBarWrapper = styled.div`
  position: relative;
  top: calc(2rem + 80px);
  z-index: 2;
  height: 20px;
  opacity: ${({ activeItemIndex }) => (activeItemIndex === 0 ? 0 : 1)};
  transition: all 0.3s;

  > div {
    width: 100%;
  }

  ${mediaquery.lg(css`
    top: calc(2rem + 85px);
  `)}
`;

const TrackerWrapper = styled.form`
  color: ${colors.white};
  width: 100%;
  margin-top: -20px;
  position: relative;
`;

const HeaderCurvedStyled = styled(HeaderCurved)`
  margin-top: -100px;

  & > div {
    min-height: 0;
    padding-top: calc(2rem + 100px);
  }
`;

const PictureWrapper = styled(Picture)`
  position: absolute !important;
`;

const TrackerQuestionsWrapper = styled.div`
  display: flex;
  overflow: hidden;
  min-height: 100vh;
`;

const TrackerList = styled.ul`
  display: flex;
  width: 100%;
  overflow: hidden;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  padding-top: 100px;
`;

const TrackerListItem = styled.li`
  position: relative;
  flex: 0 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  scroll-snap-align: start;
`;

const BtnsContainer = styled(Container)`
  position: sticky;
  bottom: 0;
  z-index: 1;
`;

const CTAButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  position: relative;
  bottom: 1rem;
  width: 100%;
  z-index: 2;
  margin-top: -50px;
`;

const ButtonStyled = styled(Button)`
  transition: all 0.3s;
  pointer-events: all;

  &[disabled] {
    opacity: 0.3;
    cursor: default;
    pointer-events: none;
  }

  &:focus {
    outline: none;
    box-shadow: none;
    border: 1px dashed ${colors.white};
  }
`;

const SlideHeader = styled(HeaderL)`
  padding-bottom: 1rem;

  ${mediaquery.md(css`
    padding-bottom: 1.5rem;
  `)}
`;

const SlideDescription = styled(Body)`
  display: block;
  margin-bottom: 2.5rem;
  font-size: 22px;
`;

const RichTextWrapper = styled.div`
  padding-top: 0.5rem;

  * {
    color: ${colors.white}!important;
  }

  ${mediaquery.md(css`
    padding-top: 1rem;
  `)}
`;

export default Tracker;
